import React, { useState, useEffect } from 'react'
import {
  Box,
  VStack,
  HStack,
  Text,
  Input,
  Select,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  FormControl,
  FormLabel,
  Grid,
  Divider,
  Tag,
  TagLabel,
  TagCloseButton,
  useColorModeValue,
  IconButton,
  Tooltip,
  Button,
  Collapse,
  useDisclosure,
  Badge,
  Icon,
  Checkbox,
} from '@chakra-ui/react'
import {
  FiPlus,
  FiChevronDown,
  FiChevronUp,
  FiFolder,
  FiCalendar,
  FiFileText,
  FiSidebar,
} from 'react-icons/fi'
import { useNavigation } from '@/hooks/useNavigation'
import { SearchParams, Folder } from '@/types'
import { useAppTranslation } from '@/contexts/LanguageContext'

interface SearchFilterSidebarProps {
  onSearch: (searchParams: SearchParams) => void
  initialParams?: Partial<SearchParams>
  // Shows a hide button in the header when given
  onCollapse?: () => void
}

// Enhanced folder tree component for better UX
function FolderSelector({
  folders,
  value,
  onChange,
}: {
  folders: Folder[]
  value?: number
  onChange: (folderId?: number) => void
}) {
  const [expandedFolders, setExpandedFolders] = useState<Set<number>>(new Set())
  const [searchTerm, setSearchTerm] = useState('')
  const { t } = useAppTranslation('search')

  const toggleFolder = (folderId: number) => {
    const newExpanded = new Set(expandedFolders)
    if (newExpanded.has(folderId)) {
      newExpanded.delete(folderId)
    } else {
      newExpanded.add(folderId)
    }
    setExpandedFolders(newExpanded)
  }

  const renderFolder = (folder: Folder, level: number = 0): React.ReactNode => {
    const isExpanded = expandedFolders.has(folder.id)
    const isSelected = value === folder.id
    const hasChildren = (folder.subfolders || folder.children || []).length > 0
    const matchesSearch =
      !searchTerm || folder.name.toLowerCase().includes(searchTerm.toLowerCase())

    if (!matchesSearch && !hasChildren) return null

    return (
      <Box key={folder.id}>
        <HStack
          spacing={2}
          p={2}
          pl={2 + level * 4}
          cursor="pointer"
          borderRadius="md"
          bg={isSelected ? 'brand.50' : 'transparent'}
          _hover={{ bg: isSelected ? 'brand.100' : 'gray.50' }}
          onClick={() => onChange(folder.id)}
          minH="32px"
          alignItems="center"
        >
          {hasChildren && (
            <IconButton
              size="xs"
              variant="ghost"
              icon={
                isExpanded ? (
                  <FiChevronDown />
                ) : (
                  <FiChevronDown style={{ transform: 'rotate(-90deg)' }} />
                )
              }
              onClick={(e) => {
                e.stopPropagation()
                toggleFolder(folder.id)
              }}
              aria-label={t('advancedSearch.toggleFolder')}
              minW="24px"
              h="24px"
            />
          )}
          {!hasChildren && <Box w="24px" h="24px" />}
          <Icon
            as={FiFolder}
            color="blue.500"
            boxSize="16px"
            flexShrink={0}
          />
          <Text
            fontSize="sm"
            fontWeight={isSelected ? 'semibold' : 'normal'}
            flex={1}
            noOfLines={1}
          >
            {folder.name}
          </Text>
          {folder.file_count !== undefined && (
            <Badge size="sm" colorScheme="gray" variant="subtle" flexShrink={0}>
              {folder.total_file_count || folder.file_count}
            </Badge>
          )}
        </HStack>

        {hasChildren && (
          <Collapse in={isExpanded}>
            <VStack spacing={0} align="stretch">
              {(folder.subfolders || folder.children || []).map((subfolder) =>
                renderFolder(subfolder, level + 1)
              )}
            </VStack>
          </Collapse>
        )}
      </Box>
    )
  }

  return (
    <VStack spacing={2} align="stretch">
      <Input
        size="sm"
        placeholder={t('advancedSearch.searchFolders')}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        mb={2}
      />
      <VStack spacing={0} align="stretch">
        <Box
          p={2}
          cursor="pointer"
          borderRadius="md"
          bg={!value ? 'brand.50' : 'transparent'}
          _hover={{ bg: !value ? 'brand.100' : 'gray.50' }}
          onClick={() => onChange(undefined)}
        >
          <HStack spacing={2}>
            <Icon as={FiFolder} color="blue.500" boxSize="16px" />
            <Text fontSize="sm" fontWeight={!value ? 'semibold' : 'normal'}>
              {t('advancedSearch.allFolders')}
            </Text>
          </HStack>
        </Box>
        {folders.map((folder) => renderFolder(folder))}
      </VStack>
    </VStack>
  )
}

export default function SearchFilterSidebar({
  onSearch,
  initialParams = {},
  onCollapse,
}: SearchFilterSidebarProps) {
  const { folderTree } = useNavigation()
  const { t } = useAppTranslation('search')

  // Enhanced file types with better categorization and proper MIME types
  const FILE_TYPE_OPTIONS = [
    { value: '', label: t('advancedSearch.fileTypes.allFiles') },
    { value: 'application/pdf', label: t('advancedSearch.fileTypes.pdf') },
    {
      value: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      label: t('advancedSearch.fileTypes.wordDocx'),
    },
    { value: 'application/msword', label: t('advancedSearch.fileTypes.wordDoc') },
    {
      value: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      label: t('advancedSearch.fileTypes.excelXlsx'),
    },
    { value: 'application/vnd.ms-excel', label: t('advancedSearch.fileTypes.excelXls') },
    {
      value: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      label: t('advancedSearch.fileTypes.powerpointPptx'),
    },
    { value: 'text/plain', label: t('advancedSearch.fileTypes.textFiles') },
    { value: 'text/csv', label: t('advancedSearch.fileTypes.csvFiles') },
    { value: 'image/jpeg', label: t('advancedSearch.fileTypes.jpegImages') },
    { value: 'image/png', label: t('advancedSearch.fileTypes.pngImages') },
    { value: 'image/svg+xml', label: t('advancedSearch.fileTypes.svgImages') },
    { value: 'image/webp', label: t('advancedSearch.fileTypes.webpImages') },
    { value: 'image/', label: t('advancedSearch.fileTypes.allImages') },
  ]

  const SEARCH_TYPES = [
    {
      value: 'hybrid',
      label: t('advancedSearch.searchTypes.smart'),
      description: t('advancedSearch.searchTypes.smartDescription'),
    },
    {
      value: 'semantic',
      label: t('advancedSearch.searchTypes.ai'),
      description: t('advancedSearch.searchTypes.aiDescription'),
    },
    {
      value: 'text',
      label: t('advancedSearch.searchTypes.text'),
      description: t('advancedSearch.searchTypes.textDescription'),
    },
  ]

  // Color mode values
  const bg = useColorModeValue('white', 'gray.800')
  const sectionBg = useColorModeValue('gray.50', 'gray.750')
  const borderColor = useColorModeValue('gray.200', 'gray.600')
  const labelColor = useColorModeValue('gray.700', 'gray.300')
  const mutedColor = useColorModeValue('gray.600', 'gray.400')

  // Section toggles
  const { isOpen: isFiltersOpen, onToggle: toggleFilters } = useDisclosure({ defaultIsOpen: true })
  const { isOpen: isDateOpen, onToggle: toggleDate } = useDisclosure()
  const { isOpen: isSizeOpen, onToggle: toggleSize } = useDisclosure()
  const { isOpen: isTagsOpen, onToggle: toggleTags } = useDisclosure()

  // Form state
  const [searchParams, setSearchParams] = useState<SearchParams>({
    query: '',
    search_type: 'hybrid',
    limit: 50,
    sort_by: 'relevance',
    sort_direction: 'desc',
    ...initialParams,
  })

  const [newTag, setNewTag] = useState('')

  // Reset form when initialParams change (compare by value, not reference)
  const initialParamsJson = JSON.stringify(initialParams)
  useEffect(() => {
    setSearchParams({
      query: '',
      search_type: 'hybrid',
      limit: 50,
      sort_by: 'relevance',
      sort_direction: 'desc',
      ...initialParams,
    })
    setNewTag('')
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialParamsJson])

  const handleInputChange = (field: keyof SearchParams, value: any) => {
    setSearchParams((prev) => ({
      ...prev,
      [field]: value || undefined,
    }))
  }

  const handleAddTag = () => {
    if (newTag.trim() && !searchParams.tags?.includes(newTag.trim())) {
      const currentTags = searchParams.tags || []
      handleInputChange('tags', [...currentTags, newTag.trim()])
      setNewTag('')
    }
  }

  const handleRemoveTag = (tagToRemove: string) => {
    const currentTags = searchParams.tags || []
    handleInputChange(
      'tags',
      currentTags.filter((tag) => tag !== tagToRemove)
    )
  }

  const handleSearch = () => {
    // Clean up empty values
    const cleanParams = Object.entries(searchParams).reduce((acc, [key, value]) => {
      if (
        value !== undefined &&
        value !== null &&
        value !== '' &&
        !(Array.isArray(value) && value.length === 0)
      ) {
        ;(acc as Record<string, any>)[key] = value
      }
      return acc
    }, {} as SearchParams)

    onSearch(cleanParams)
  }

  const hasActiveFilters = Object.keys(searchParams).some(
    (key) =>
      key !== 'query' &&
      key !== 'search_type' &&
      key !== 'limit' &&
      key !== 'sort_by' &&
      key !== 'sort_direction' &&
      searchParams[key as keyof SearchParams] !== undefined
  )

  const activeFiltersCount = Object.keys(searchParams).filter(
    (key) =>
      key !== 'query' &&
      key !== 'search_type' &&
      key !== 'limit' &&
      key !== 'sort_by' &&
      key !== 'sort_direction' &&
      searchParams[key as keyof SearchParams] !== undefined
  ).length

  return (
    <Box bg={bg} display="flex" flexDirection="column" h="full">
      {/* Header */}
      <HStack
        justify="space-between"
        p={4}
        borderBottom="1px solid"
        borderColor={borderColor}
        bg={sectionBg}
        flexShrink={0}
      >
        <HStack spacing={2}>
          <Text fontWeight="semibold">{t('advancedSearch.title')}</Text>
          {hasActiveFilters && (
            <Badge colorScheme="brand" variant="subtle" size="sm">
              {t('advancedSearch.filtered')}
            </Badge>
          )}
        </HStack>
        {onCollapse && (
          <Tooltip label={t('results.hideFilters')} placement="right">
            <IconButton
              aria-label={t('results.hideFilters')}
              icon={<FiSidebar />}
              variant="ghost"
              size="sm"
              color={mutedColor}
              onClick={onCollapse}
            />
          </Tooltip>
        )}
      </HStack>

      {/* Scrollable Content */}
      <Box overflowY="auto" flex={1}>
        <VStack
          spacing={0}
          align="stretch"
          p={4}
          sx={{
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'gray.200',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: 'gray.300',
            },
          }}
        >
          {/* Basic Search */}
          <VStack spacing={3} align="stretch" mb={4}>
            <FormControl>
              <FormLabel fontSize="sm" color={labelColor}>
                {t('advancedSearch.searchQuery')}
              </FormLabel>
              <Input
                value={searchParams.query}
                onChange={(e) => handleInputChange('query', e.target.value)}
                placeholder={t('advancedSearch.searchQueryPlaceholder')}
                size="sm"
              />
            </FormControl>

            <VStack spacing={3} align="stretch">
              <FormControl>
                <FormLabel fontSize="sm" color={labelColor}>
                  {t('advancedSearch.searchType')}
                </FormLabel>
                <Select
                  value={searchParams.search_type || 'hybrid'}
                  onChange={(e) => handleInputChange('search_type', e.target.value)}
                  size="sm"
                >
                  {SEARCH_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm" color={labelColor}>
                  {t('advancedSearch.results')}
                </FormLabel>
                <NumberInput
                  value={searchParams.limit || 50}
                  onChange={(_, value) => handleInputChange('limit', value)}
                  min={10}
                  max={500}
                  size="sm"
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>
            </VStack>
          </VStack>

          <Divider my={2} />

          {/* Filters Section */}
          <Box>
            <Button
              variant="ghost"
              size="sm"
              leftIcon={isFiltersOpen ? <FiChevronUp /> : <FiChevronDown />}
              onClick={toggleFilters}
              justifyContent="flex-start"
              fontWeight="semibold"
              color={labelColor}
              p={2}
              w="full"
            >
              {t('advancedSearch.contentFilters')}
            </Button>

            <Collapse in={isFiltersOpen}>
              <VStack spacing={4} align="stretch" pl={4} pt={2} pb={3}>
                {/* Folder Filter - Full Width */}
                <FormControl>
                  <FormLabel fontSize="sm" color={labelColor}>
                    <HStack spacing={1}>
                      <FiFolder size={14} />
                      <Text>{t('advancedSearch.folder')}</Text>
                    </HStack>
                  </FormLabel>
                  <Box
                    border="1px solid"
                    borderColor={borderColor}
                    borderRadius="md"
                    p={2}
                    maxH="250px"
                    overflowY="auto"
                    sx={{
                      '&::-webkit-scrollbar': {
                        width: '6px',
                      },
                      '&::-webkit-scrollbar-track': {
                        background: 'transparent',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        background: 'gray.200',
                        borderRadius: '4px',
                      },
                      '&::-webkit-scrollbar-thumb:hover': {
                        background: 'gray.300',
                      },
                    }}
                  >
                    <FolderSelector
                      folders={folderTree}
                      value={searchParams.folder_id}
                      onChange={(folderId) => handleInputChange('folder_id', folderId)}
                    />
                  </Box>
                  {searchParams.folder_id && (
                    <Checkbox
                      mt={2}
                      size="sm"
                      isChecked={!!searchParams.include_subfolders}
                      onChange={(e) =>
                        handleInputChange('include_subfolders', e.target.checked || undefined)
                      }
                    >
                      <Text fontSize="sm">{t('advancedSearch.includeSubfolders')}</Text>
                    </Checkbox>
                  )}
                </FormControl>

                {/* File Type and Filename */}
                <VStack spacing={3} align="stretch">
                  <FormControl>
                    <FormLabel fontSize="sm" color={labelColor}>
                      <HStack spacing={1}>
                        <FiFileText size={14} />
                        <Text>{t('advancedSearch.fileType')}</Text>
                      </HStack>
                    </FormLabel>
                    <Select
                      value={searchParams.mime_type || ''}
                      onChange={(e) => handleInputChange('mime_type', e.target.value)}
                      size="sm"
                    >
                      {FILE_TYPE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl>
                    <FormLabel fontSize="sm" color={labelColor}>
                      {t('advancedSearch.filenameContains')}
                    </FormLabel>
                    <Input
                      value={searchParams.file_name || ''}
                      onChange={(e) => handleInputChange('file_name', e.target.value)}
                      placeholder={t('advancedSearch.filenameContainsPlaceholder')}
                      size="sm"
                    />
                  </FormControl>
                </VStack>
              </VStack>
            </Collapse>
          </Box>

          <Divider my={2} />

          {/* Date Filters */}
          <Box>
            <Button
              variant="ghost"
              size="sm"
              leftIcon={isDateOpen ? <FiChevronUp /> : <FiChevronDown />}
              rightIcon={<FiCalendar size={14} />}
              onClick={toggleDate}
              justifyContent="space-between"
              fontWeight="semibold"
              color={labelColor}
              p={2}
              w="full"
            >
              {t('advancedSearch.dateRange')}
            </Button>

            <Collapse in={isDateOpen}>
              <Grid templateColumns="repeat(2, 1fr)" gap={3} pl={4} pt={2} pb={3}>
                <FormControl>
                  <FormLabel fontSize="sm" color={labelColor}>
                    {t('advancedSearch.fromDate')}
                  </FormLabel>
                  <Input
                    type="date"
                    value={searchParams.date_from || ''}
                    onChange={(e) => handleInputChange('date_from', e.target.value)}
                    size="sm"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" color={labelColor}>
                    {t('advancedSearch.toDate')}
                  </FormLabel>
                  <Input
                    type="date"
                    value={searchParams.date_to || ''}
                    onChange={(e) => handleInputChange('date_to', e.target.value)}
                    size="sm"
                  />
                </FormControl>
              </Grid>
            </Collapse>
          </Box>

          <Divider my={2} />

          {/* Size Filters */}
          <Box>
            <Button
              variant="ghost"
              size="sm"
              leftIcon={isSizeOpen ? <FiChevronUp /> : <FiChevronDown />}
              onClick={toggleSize}
              justifyContent="flex-start"
              fontWeight="semibold"
              color={labelColor}
              p={2}
              w="full"
            >
              {t('advancedSearch.fileSize')}
            </Button>

            <Collapse in={isSizeOpen}>
              <VStack spacing={3} align="stretch" pl={4} pt={2} pb={3}>
                <FormControl>
                  <FormLabel fontSize="sm" color={labelColor}>
                    {t('advancedSearch.minSize')}
                  </FormLabel>
                  <NumberInput
                    value={searchParams.file_size_min_mb || ''}
                    onChange={(_, value) =>
                      handleInputChange('file_size_min_mb', value || undefined)
                    }
                    min={0}
                    precision={2}
                    size="sm"
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" color={labelColor}>
                    {t('advancedSearch.maxSize')}
                  </FormLabel>
                  <NumberInput
                    value={searchParams.file_size_max_mb || ''}
                    onChange={(_, value) =>
                      handleInputChange('file_size_max_mb', value || undefined)
                    }
                    min={0}
                    precision={2}
                    size="sm"
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>
              </VStack>
            </Collapse>
          </Box>

          <Divider my={2} />

          {/* Tags */}
          <Box>
            <Button
              variant="ghost"
              size="sm"
              leftIcon={isTagsOpen ? <FiChevronUp /> : <FiChevronDown />}
              onClick={toggleTags}
              justifyContent="flex-start"
              fontWeight="semibold"
              color={labelColor}
              p={2}
              w="full"
            >
              {t('advancedSearch.tags')}{' '}
              {searchParams.tags?.length ? `(${searchParams.tags.length})` : ''}
            </Button>

            <Collapse in={isTagsOpen}>
              <VStack spacing={3} align="stretch" pl={4} pt={2} pb={3}>
                <HStack>
                  <Input
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    placeholder={t('advancedSearch.addTagPlaceholder')}
                    size="sm"
                    onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
                  />
                  <Tooltip label={t('advancedSearch.addTagTooltip')}>
                    <IconButton
                      size="sm"
                      icon={<FiPlus />}
                      onClick={handleAddTag}
                      isDisabled={!newTag.trim()}
                      aria-label={t('advancedSearch.addTag')}
                    />
                  </Tooltip>
                </HStack>

                {searchParams.tags && searchParams.tags.length > 0 && (
                  <HStack spacing={2} wrap="wrap">
                    {searchParams.tags.map((tag) => (
                      <Tag key={tag} size="sm" variant="solid" colorScheme="brand">
                        <TagLabel>{tag}</TagLabel>
                        <TagCloseButton onClick={() => handleRemoveTag(tag)} />
                      </Tag>
                    ))}
                  </HStack>
                )}
              </VStack>
            </Collapse>
          </Box>
        </VStack>
      </Box>

      {/* Footer */}
      <HStack
        justify="space-between"
        p={4}
        borderTop="1px solid"
        borderColor={borderColor}
        bg={sectionBg}
        flexShrink={0}
      >
        <Text fontSize="xs" color={mutedColor}>
          {t('advancedSearch.filtersActive', { count: activeFiltersCount })}
        </Text>

        <HStack spacing={2}>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setSearchParams({
                query: searchParams.query || '',
                search_type: 'hybrid',
                limit: 50,
                sort_by: 'relevance',
                sort_direction: 'desc',
              })
              setNewTag('')
            }}
          >
            {t('advancedSearch.reset')}
          </Button>
          <Button size="sm" colorScheme="brand" onClick={handleSearch}>
            {t('advancedSearch.search')}
          </Button>
        </HStack>
      </HStack>
    </Box>
  )
}
